/**
 * Tab "Bedrock AI" de /ia-native/observability (rehecho 09/10/2026, feedback de Sol:
 * "solo quiero saber qué gasta la plataforma, la infraestructura, la gente que
 * consulta, todo todo").
 *
 * Arriba, un ÁRBOL de gasto con totales y filas desplegables:
 *   Total del período
 *   ├─ Winclap Brain → personas que consultan / indexación automática / pruebas → features → quién lo usa
 *   ├─ Back Office → por apartado → features → quién lo usa; pruebas y bots del BO
 *   ├─ Sin registrar (lo real de la cuenta menos lo logueado)
 *   └─ Infraestructura AWS de la plataforma → Lambda / DynamoDB / Aurora / S3
 * La cuenta es `summarizeBedrockRows` (shared/bedrock-features.ts, testeada). Abajo,
 * el gráfico por día y el detalle técnico plegado (por feature, modelo, usuario).
 */
import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TreeRow } from '@/components/ui/tree-row';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { LoaderBlock, fmtUSDCents, fmtNum, fmtPct } from './shared';
import { useObsBedrockDaily, useObsBedrockReal, useObsBedrockByUser, useObsAwsCosts } from '@/hooks/useObservability';
import {
  BEDROCK_BLOCKS, BEDROCK_MODULES, BEDROCK_WINDOWS, bedrockBlockOf, bedrockFeatureMeta, bedrockModelMeta, isEmbeddingModel, summarizeBedrockRows,
  type BedrockBlock, type BedrockModule, type BedrockWindowDays, type BedrockFeatureTotal,
} from '../../../../shared/bedrock-features';

const BLOCK_COLOR: Record<BedrockBlock, string> = {
  people_brain: '#6366f1',
  people_bo:    '#10b981',
  sync:         '#f59e0b',
  qa:           '#94a3b8',
};

const MODULE_CLASS: Record<BedrockModule, string> = {
  brain:        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  ta:           'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  people:       'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  performance:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  partnerships: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  oml:          'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  supportia:    'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  vault:        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  platform:     'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const num = (v: unknown) => Number(v ?? 0) || 0;
const shortDay = (d: string) => d.slice(5); // "MM-DD"
const signed = (v: number) => `${v < 0 ? '−' : ''}${fmtUSDCents(Math.abs(v))}`;

const thClass = 'px-4 py-2 text-left';
const thRight = 'px-4 py-2 text-right';

interface UserRow { user_email: string; feature: string; actor_kind?: string | null; calls: number; cost_usd: number }

export function ObsBedrockTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState<BedrockWindowDays>(30);
  const daily = useObsBedrockDaily(days);
  const real = useObsBedrockReal(days);
  const byUser = useObsBedrockByUser(days);
  const infra = useObsAwsCosts(days);

  const tr = (k: string, o?: Record<string, unknown>) => t(`observability.bedrock.tree.${k}`, o);
  const blockLabel = (b: BedrockBlock) => t(`observability.bedrock.block_${b}`);
  const moduleLabel = (m: BedrockModule) => t(`observability.bedrock.module_${m}`);

  const agg = useMemo(() => {
    const rows = daily.data ?? [];
    const usage = real.data?.usage ?? [];
    const billed = real.data?.billed ?? [];
    const summary = summarizeBedrockRows(rows);

    // Serie diaria para el gráfico: bloques apilados + lo real de CloudWatch.
    const byDay = new Map<string, Record<string, number>>();
    const dayRow = (d: string) => {
      let r = byDay.get(d);
      if (!r) { r = { people_brain: 0, people_bo: 0, sync: 0, qa: 0, real: 0 }; byDay.set(d, r); }
      return r;
    };
    const byStage = new Map<string, number>();
    let loggedInput = 0, loggedCache = 0;
    for (const r of rows) {
      dayRow(r.day)[bedrockBlockOf(r.feature, r.actor_kind)] += num(r.cost_usd);
      byStage.set(r.stage, (byStage.get(r.stage) ?? 0) + num(r.cost_usd));
      loggedInput += num(r.input_tokens) + num(r.cache_read_tokens);
      loggedCache += num(r.cache_read_tokens);
    }
    const byModel = new Map<string, { modelId: string; invocations: number; input: number; output: number; cacheRead: number; usd: number }>();
    // Lo registrado por modelo, para decir de QUÉ modelo es lo que falta (Haiku vs Sonnet).
    const loggedByModel = new Map<string, number>();
    for (const r of rows) loggedByModel.set(r.model, (loggedByModel.get(r.model) ?? 0) + num(r.cost_usd));
    let realTotal = 0, embeddingsUsd = 0, embeddingsCalls = 0;
    const unloggedByModel = new Map<string, number>();
    for (const u of usage) {
      dayRow(u.day).real += u.costUsd;
      realTotal += u.costUsd;
      // Titan (embeddings de indexación y búsqueda) no se loguea por llamada (10-25k por día,
      // ~$0,2): se muestra como fila propia del Brain, no como "sin registrar".
      if (isEmbeddingModel(u.modelId)) { embeddingsUsd += u.costUsd; embeddingsCalls += u.invocations; }
      else unloggedByModel.set(u.modelId, (unloggedByModel.get(u.modelId) ?? 0) + u.costUsd);
      let m = byModel.get(u.modelId);
      if (!m) { m = { modelId: u.modelId, invocations: 0, input: 0, output: 0, cacheRead: 0, usd: 0 }; byModel.set(u.modelId, m); }
      m.invocations += u.invocations; m.input += u.inputTokens; m.output += u.outputTokens; m.cacheRead += u.cacheReadTokens; m.usd += u.costUsd;
    }
    const billedTotal = billed.reduce((s, b) => s + b.usd, 0);

    // Quién lo usa: las filas por usuario traen actor_kind (ACTOR_SQL), así que el
    // bloque sale de la misma regla que el resto (bedrockBlockOf), sin otra lista de bots.
    const users = ((byUser.data ?? []) as UserRow[]).map((u) => ({
      ...u, usd: num(u.cost_usd), calls: num(u.calls),
      block: bedrockBlockOf(u.feature, u.actor_kind), module: bedrockFeatureMeta(u.feature).module,
    }));
    const usersOf = (pred: (u: (typeof users)[number]) => boolean) => {
      const m = new Map<string, { email: string; usd: number; calls: number }>();
      for (const u of users) {
        if (!pred(u)) continue;
        const e = m.get(u.user_email) ?? { email: u.user_email, usd: 0, calls: 0 };
        e.usd += u.usd; e.calls += u.calls; m.set(u.user_email, e);
      }
      return [...m.values()].sort((a, b) => b.usd - a.usd);
    };

    const infraCosts = infra.data?.costs ?? [];
    const infraTotal = infraCosts.reduce((s, c) => s + c.cost, 0);
    return {
      summary,
      chart: [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, v]) => ({ day, ...v })),
      models: [...byModel.values()].sort((a, b) => b.usd - a.usd),
      embeddingsUsd, embeddingsCalls,
      unloggedByModel: [...unloggedByModel.entries()].map(([m, usd]) => ({ modelId: m, usd: usd - (loggedByModel.get(m) ?? 0) })),
      stages: [...byStage.entries()].sort((a, b) => b[1] - a[1]),
      realTotal, billedTotal, loggedInput, loggedCache,
      users, usersOf, infraCosts, infraTotal,
    };
  }, [daily.data, real.data, byUser.data, infra.data]);

  const hasReal = real.data?.usage != null;
  const s = agg.summary;
  const bedrockTotal = hasReal ? agg.realTotal : s.total;
  // Sin registrar = lo real menos los embeddings (fila propia) menos lo que el código logueó.
  const unlogged = hasReal ? agg.realTotal - agg.embeddingsUsd - s.total : null;
  const unloggedDetail = agg.unloggedByModel.filter((m) => Math.abs(m.usd) >= 0.01).map((m) => `${bedrockModelMeta(m.modelId).label} ${signed(m.usd)}`).join(' · ');
  const grandTotal = bedrockTotal + agg.infraTotal;
  // El total se muestra recién cuando llegaron lo real y la infra: antes "saltaba" al cargar cada pieza.
  const totalPending = real.isLoading || infra.isLoading;
  const usersReady = !byUser.isLoading && !byUser.isError;
  const pct = (part: number, whole: number = grandTotal) => (whole > 0 ? fmtPct((part / whole) * 100) : '—');
  const brainTotal = s.brain.total + agg.embeddingsUsd;
  const boQa = s.bo.qa;
  const boTotal = s.bo.total;
  // people_brain y sync son por definición del Brain; qa se parte por módulo (Brain vs BO).
  const featuresOf = (block: BedrockBlock, module?: BedrockModule) =>
    s.features.filter((f) => f.block === block && (!module || f.module === module));

  const featureRows = (list: BedrockFeatureTotal[], depth: 2 | 3) => list.map((f) => (
    <TreeRow key={`${f.feature}|${f.block}`} depth={depth} amount={fmtUSDCents(f.usd)} pct={pct(f.usd)}
      label={<span><span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${MODULE_CLASS[f.module]}`}>{bedrockFeatureMeta(f.feature).label}</span> <span className="ml-1 font-mono text-[11px] text-muted-foreground">{f.feature}</span></span>}
      meta={tr('calls', { n: fmtNum(f.calls) })} />
  ));
  // Sin lista (cargando, error, o el apartado no entró en el top por gasto) no se afirma "0 personas".
  const peopleMeta = (list: unknown[]) => (usersReady && list.length > 0 ? tr('people_n', { count: list.length }) : undefined);
  const TOP_USERS = 25;
  const userRows = (list: Array<{ email: string; usd: number; calls: number }>, depth: 2 | 3) => list.length === 0 ? null : (
    <TreeRow depth={depth} label={tr('who')} amount={fmtUSDCents(list.reduce((a, u) => a + u.usd, 0))} pct={null}
      meta={tr('people_n', { count: list.length })}>
      {list.slice(0, TOP_USERS).map((u) => (
        <TreeRow key={u.email} depth={3} label={<span className="font-mono text-xs" title={u.email}>{u.email}</span>} amount={fmtUSDCents(u.usd)} pct={pct(u.usd)} meta={tr('calls', { n: fmtNum(u.calls) })} />
      ))}
      {list.length > TOP_USERS && (
        <TreeRow depth={3} label={<span className="text-xs text-muted-foreground">{tr('more_n', { n: list.length - TOP_USERS })}</span>}
          amount={fmtUSDCents(list.slice(TOP_USERS).reduce((a, u) => a + u.usd, 0))} pct={null} />
      )}
    </TreeRow>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs text-muted-foreground">{t('observability.bedrock.intro')}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('observability.bedrock.period_label')}</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v) as BedrockWindowDays)}>
            <SelectTrigger className="h-8 w-40 text-sm" aria-label={t('observability.bedrock.period_label')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {BEDROCK_WINDOWS.map((w) => <SelectItem key={w} value={String(w)}>{t(`observability.bedrock.period_${w}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {daily.isError && (
        <Card className="flex items-start gap-3 p-4 text-amber-600">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{t('observability.bedrock.load_error', { error: daily.error?.message ?? '' })}</p>
        </Card>
      )}
      {daily.isLoading && <LoaderBlock />}

      {!daily.isLoading && !daily.isError && (<>
      {/* ── El árbol ───────────────────────────────────────────────────────── */}
      <Card>
        <div className="border-b px-4 py-3">
          <h3 className="text-base font-semibold">{tr('title')}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{tr('subtitle')}</p>
        </div>
        <TreeRow emphasis label={tr('total')} amount={totalPending ? '…' : fmtUSDCents(grandTotal)} pct={null} className="bg-muted/30" />

        {/* Winclap Brain */}
        <TreeRow emphasis defaultOpen label={tr('brain')} description={tr('brain_desc')} amount={fmtUSDCents(brainTotal)} pct={pct(brainTotal)}>
          <TreeRow depth={1} label={tr('brain_people')} description={tr('brain_people_desc')} amount={fmtUSDCents(s.byBlock.people_brain)} pct={pct(s.byBlock.people_brain)}
            meta={peopleMeta(agg.usersOf((u) => u.block === 'people_brain'))}>
            {featureRows(featuresOf('people_brain'), 2)}
            {userRows(agg.usersOf((u) => u.block === 'people_brain'), 2)}
          </TreeRow>
          <TreeRow depth={1} label={tr('brain_sync')} description={tr('brain_sync_desc')} amount={fmtUSDCents(s.byBlock.sync)} pct={pct(s.byBlock.sync)}>
            {featureRows(featuresOf('sync'), 2)}
          </TreeRow>
          <TreeRow depth={1} label={tr('brain_qa')} description={tr('brain_qa_desc')} amount={fmtUSDCents(s.brain.qa)} pct={pct(s.brain.qa)}>
            {featureRows(featuresOf('qa', 'brain'), 2)}
          </TreeRow>
          {hasReal && (
            <TreeRow depth={1} label={tr('brain_embeddings')} description={tr('brain_embeddings_desc')} amount={fmtUSDCents(agg.embeddingsUsd)} pct={pct(agg.embeddingsUsd)}
              meta={tr('calls', { n: fmtNum(agg.embeddingsCalls) })} />
          )}
        </TreeRow>

        {/* Back Office por apartado */}
        <TreeRow emphasis defaultOpen label={tr('bo')} description={tr('bo_desc')} amount={fmtUSDCents(boTotal)} pct={pct(boTotal)}>
          {BEDROCK_MODULES.filter((m) => m !== 'brain' && (s.byModulePeople[m] ?? 0) > 0).sort((a, b) => (s.byModulePeople[b] ?? 0) - (s.byModulePeople[a] ?? 0)).map((m) => (
            <TreeRow key={m} depth={1} label={moduleLabel(m)} amount={fmtUSDCents(s.byModulePeople[m] ?? 0)} pct={pct(s.byModulePeople[m] ?? 0)}
              meta={peopleMeta(agg.usersOf((u) => u.block === 'people_bo' && u.module === m))}>
              {featureRows(featuresOf('people_bo', m), 2)}
              {userRows(agg.usersOf((u) => u.block === 'people_bo' && u.module === m), 2)}
            </TreeRow>
          ))}
          {boQa > 0 && (
            <TreeRow depth={1} label={tr('bo_qa')} description={tr('bo_qa_desc')} amount={fmtUSDCents(boQa)} pct={pct(boQa)}>
              {featureRows(s.features.filter((f) => f.block === 'qa' && f.module !== 'brain'), 2)}
            </TreeRow>
          )}
          {boTotal === 0 && <TreeRow depth={1} label={tr('no_cost')} amount="" pct={null} />}
        </TreeRow>

        {/* Sin registrar */}
        {unlogged != null && (
          <TreeRow emphasis label={tr('unlogged')}
            description={`${unlogged < 0 ? t('observability.bedrock.stat_unlogged_sub_neg') : tr('unlogged_desc')}${unloggedDetail ? ` · ${unloggedDetail}` : ''}`}
            amount={signed(unlogged)} pct={unlogged > 0 ? pct(unlogged) : '—'} />
        )}

        {/* Infraestructura: con error se dice, no se muestra $0 */}
        <TreeRow emphasis label={tr('infra')}
          description={infra.isError ? <span className="text-amber-600">{tr('infra_unavailable', { error: infra.error?.message ?? '' })}</span> : tr('infra_desc')}
          amount={infra.isLoading ? '…' : infra.isError ? '—' : fmtUSDCents(agg.infraTotal)} pct={infra.isError || infra.isLoading ? null : pct(agg.infraTotal)}>
          {agg.infraCosts.map((c) => (
            <TreeRow key={c.service} depth={1} label={c.service} description={c.detail} amount={fmtUSDCents(c.cost)} pct={pct(c.cost)} />
          ))}
        </TreeRow>

        <div className="space-y-1 px-4 py-3 text-xs text-muted-foreground">
          {real.data?.billed != null && agg.billedTotal > 0 && <p>{tr('billed_ref', { usd: fmtUSDCents(agg.billedTotal) })}</p>}
          {real.isError && <p className="text-amber-600">{t('observability.bedrock.real_unavailable', { error: real.error?.message ?? '' })}</p>}
          {real.data?.usageError && <p className="text-amber-600">{t('observability.bedrock.real_unavailable', { error: real.data.usageError })}</p>}
          {real.data?.billedError && <p className="text-amber-600">{t('observability.bedrock.billed_unavailable', { error: real.data.billedError })}</p>}
          {byUser.isError && <p className="text-amber-600">{t('observability.bedrock.load_error', { error: byUser.error?.message ?? '' })}</p>}
        </div>
      </Card>

      {/* ── Por día ────────────────────────────────────────────────────────── */}
      <Card className="p-4">
        <h3 className="mb-1 text-sm font-semibold">{t('observability.bedrock.chart_title')}</h3>
        <p className="mb-3 text-xs text-muted-foreground">{t('observability.bedrock.chart_subtitle')}</p>
        {agg.chart.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('observability.bedrock.no_daily_data')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={agg.chart} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} width={48} />
              <Tooltip formatter={(v) => fmtUSDCents(Number(v))} labelFormatter={(d) => String(d)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {/* sin animación: la captura del bot de staging salía con las barras en 0 */}
              {BEDROCK_BLOCKS.map((b) => <Bar key={b} dataKey={b} name={blockLabel(b)} stackId="a" fill={BLOCK_COLOR[b]} isAnimationActive={false} />)}
              {hasReal && <Line type="monotone" dataKey="real" name={t('observability.bedrock.chart_real')} stroke="#0f172a" strokeWidth={2} dot={false} isAnimationActive={false} />}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {agg.stages.map(([stage, usd]) => (
            <Badge key={stage} variant="secondary" className="font-normal">{t('observability.bedrock.stage_prefix')} {stage} · <span className="font-semibold">{fmtUSDCents(usd)}</span></Badge>
          ))}
          <Badge variant="outline" className="font-normal">{t('observability.bedrock.col_cache')} · <span className="font-semibold">{pct(agg.loggedCache, agg.loggedInput)}</span></Badge>
        </div>
      </Card>

      {/* ── Detalle técnico, plegado ───────────────────────────────────────── */}
      <details className="group">
        <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">{tr('detail_toggle')}</summary>
        <div className="mt-4 space-y-6">
          <Card>
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold">{t('observability.bedrock.by_feature_title')}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('observability.bedrock.by_feature_subtitle')}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className={thClass}>{t('observability.bedrock.col_module')}</th>
                    <th className={thClass}>{t('observability.bedrock.col_feature')}</th>
                    <th className={thClass}>{t('observability.bedrock.col_traffic')}</th>
                    <th className={thRight}>{t('observability.bedrock.col_calls')}</th>
                    <th className={thRight}>{t('observability.bedrock.col_cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.features.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">{t('observability.bedrock.no_data')}</td></tr>
                  ) : s.features.map((f) => (
                    <tr key={`${f.feature}|${f.block}`} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs font-medium">{moduleLabel(f.module)}</Badge></td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${MODULE_CLASS[f.module]}`}>{bedrockFeatureMeta(f.feature).label}</span>
                        <span className="ml-2 font-mono text-[11px] text-muted-foreground">{f.feature}</span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{blockLabel(f.block)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(f.calls)}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums">{fmtUSDCents(f.usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold">{t('observability.bedrock.by_model_title')}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('observability.bedrock.by_model_subtitle')}</p>
            </div>
            {real.isLoading ? (
              <div className="flex justify-center py-8"><LoaderBlock /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                      <th className={thClass}>{t('observability.bedrock.col_model')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_invocations')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_tokens_in')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_cache_read')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_tokens_out')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agg.models.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">{t('observability.bedrock.no_data')}</td></tr>
                    ) : agg.models.map((m) => (
                      <tr key={m.modelId} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-2.5"><span className="font-medium">{bedrockModelMeta(m.modelId).label}</span><span className="ml-2 font-mono text-[11px] text-muted-foreground">{m.modelId}</span></td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(m.invocations)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(m.input)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{fmtNum(m.cacheRead)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(m.output)}</td>
                        <td className="px-4 py-2.5 text-right font-medium tabular-nums">{fmtUSDCents(m.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {real.data?.billed != null && <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">{t('observability.bedrock.billed_lag')}</p>}
          </Card>

          <Card>
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold">{t('observability.bedrock.by_user_title')}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('observability.bedrock.by_user_subtitle')}</p>
            </div>
            {byUser.isLoading ? (
              <div className="flex justify-center py-8"><LoaderBlock /></div>
            ) : byUser.isError ? (
              <p className="px-4 py-6 text-sm text-amber-600">{t('observability.bedrock.load_error', { error: byUser.error?.message ?? '' })}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                      <th className={thClass}>{t('observability.bedrock.col_user')}</th>
                      <th className={thClass}>{t('observability.bedrock.col_module')}</th>
                      <th className={thClass}>{t('observability.bedrock.col_feature')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_calls')}</th>
                      <th className={thRight}>{t('observability.bedrock.col_cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agg.users.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">{t('observability.bedrock.no_user_data')}</td></tr>
                    ) : agg.users.map((u, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{u.user_email}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs">{moduleLabel(u.module)}</Badge></td>
                        <td className="px-4 py-2.5"><span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${MODULE_CLASS[u.module]}`}>{bedrockFeatureMeta(u.feature).label}</span></td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(u.calls)}</td>
                        <td className="px-4 py-2.5 text-right font-medium tabular-nums">{fmtUSDCents(u.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </details>
      </>)}
    </div>
  );
}
